import { Button, ButtonLevel, ButtonSize, SelectedFileStatus, Text, TextColor, TextSize } from '@vizit/atoms';
import { ChevronDownIcon, XCloseIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { useState } from 'react';
import { useUploadQueue } from './provider';
import { UploadQueueItem } from './upload-queue-item';

interface UploadQueueProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Displays the upload queue in the lower right corner
 */
function UploadQueue(props: UploadQueueProps) {
  const { className, ...other } = props;
  const { items, onRemoveItem, onClearCompleted } = useUploadQueue();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const completedCount = items.filter(
    (item) => item.status === SelectedFileStatus.Success || item.status === SelectedFileStatus.Error,
  ).length;

  const uploadingCount = items.filter((item) => item.status === SelectedFileStatus.Uploading).length;

  const pendingCount = items.filter((item) => item.status === SelectedFileStatus.Ready).length;

  if (items.length === 0) {
    return null;
  }

  const getStatusText = () => {
    const inProgressCount = uploadingCount + pendingCount;
    if (inProgressCount > 0) {
      return `Uploading ${completedCount} of ${items.length} assets`;
    }
    if (completedCount === items.length) {
      return `${completedCount} asset${completedCount !== 1 ? 's' : ''} uploaded`;
    }
    return `${items.length} asset${items.length !== 1 ? 's' : ''} queued`;
  };

  const handleClose = () => {
    onClearCompleted();
  };

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => !prev);
  };

  return (
    <div
      className={cx(
        'fixed bottom-4 right-4 z-[9999] flex flex-col bg-white shadow-lg border border-secondary min-w-[480px] max-w-[480px]',
        className,
      )}
      aria-label="Upload queue"
      {...other}
    >
      <div className="flex items-center justify-between pl-8 pr-2 py-4 border-b border-secondary bg-secondary">
        <Text color={TextColor.Secondary} size={TextSize.Sm}>
          {getStatusText()}
        </Text>
        <div className="flex items-center">
          <Button
            aria-label={isCollapsed ? 'Expand queue' : 'Collapse queue'}
            level={ButtonLevel['Text-Secondary']}
            size={ButtonSize.Sm}
            iconStart={
              <ChevronDownIcon
                size={20}
                className={cx('transition-transform duration-200', isCollapsed && 'rotate-180')}
              />
            }
            onPress={toggleCollapsed}
          />
          <Button
            aria-label="Close upload queue"
            level={ButtonLevel['Text-Secondary']}
            size={ButtonSize.Sm}
            iconStart={<XCloseIcon size={20} />}
            onPress={handleClose}
          />
        </div>
      </div>
      {!isCollapsed && (
        <div className="flex flex-col overflow-y-auto max-h-[400px]">
          {items.map((item) => (
            <div key={item.id} className="animate-in slide-in-from-right-full duration-300">
              <UploadQueueItem item={item} onRemove={onRemoveItem} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { UploadQueue };
export type { UploadQueueProps };
